import React, { useState, useEffect } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from '../../../shared/store/hooks';
import { rewardsSliceAPI } from '../store/rewardsSlice';
import { settingsSliceAPI } from '../../settings/store/settingsSlice';
import { useAuth } from '../../auth/hooks/useAuth';
import { useMerchantAuth } from '../../../contexts/AuthContext';
import { createLogger } from '../../../shared/config/logger';
import { extractErrorMessage } from '../../../shared/utils/errorHandling';
import { RewardAnalytics } from '../components/RewardAnalytics';
import { RewardList } from '../components/RewardList';
import { RewardForm } from '../components/RewardForm';
import { RewardDeleteModal } from '../components/RewardDeleteModal';
import { TierLimitWarningModal } from '../../../shared/components/TierLimitWarningModal';
import { VISIT_AND_PUNCH_CARD_REWARDS_UI_ENABLED, BIRTHDAY_REWARDS_UI_ENABLED } from '../../../shared/config/featureFlags';
const logger = createLogger('Rewards.jsx');

export default function Rewards() {
    const [showModal, setShowModal] = useState(false);
    const [selectedRewardId, setSelectedRewardId] = useState(null);
    const [errors, setErrors] = useState({});
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [warningMessage, setWarningMessage] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 6;
    const [formLoading, setFormLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({
        type: 'points',
        title: '',
        description: '',
        cost: 0,
        max_redemptions: 1,
        hasMaxRedemptions: false,
        birthday_window: 'week',
    });
    const [success, setSuccess] = useState(false);

    // Redux state management
    const dispatch = useAppDispatch();
    const rewardsObj = useAppSelector((state) => state.rewards.rewards);
    const rewards = Object.values(rewardsObj);
    const loading = useAppSelector((state) => state.rewards.loading);
    const error = useAppSelector((state) => state.rewards.error);
    const hasLoaded = useAppSelector((state) => state.rewards.hasLoaded);
    const analytics = useAppSelector((state) => state.rewards.analytics);
    const rewardsettings = useAppSelector((state) => state.settings.rewardsettings);
    const {
        state: { userId },
    } = useAuth();
    const { hasPermission } = useMerchantAuth();

    useEffect(() => {
        if (rewardsettings === undefined || rewardsettings?.point_per_dollar === undefined) {
            dispatch(settingsSliceAPI.fetchRewardSettings());
        }
    }, [dispatch, rewardsettings]);


    const totalPages = Math.ceil(rewards.length / itemsPerPage);
    const paginatedRewards = rewards.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handleRefresh = async () => {
        if (userId) {
            try {
                await dispatch(rewardsSliceAPI.fetchRewards()).unwrap();
                await dispatch(rewardsSliceAPI.fetchAnalytics(userId)).unwrap();
            } catch (err) {
                const message = extractErrorMessage(err);
                logger.error('Failed to refresh rewards', { userId, error: message });
            }
        }
    };

    const handleFormChange = (key, value) => {
        const TITLE_MAX_LENGTH = 60;
        const DESCRIPTION_MAX_LENGTH = 85;
        if (key === 'title' && value.length > TITLE_MAX_LENGTH) {return;}
        if (key === 'description' && value.length > DESCRIPTION_MAX_LENGTH) {return;}
        if (key === 'hasMaxRedemptions' && !value) {
            setForm((prev) => ({ ...prev, hasMaxRedemptions: false, max_redemptions: 1 }));
            return;
        }
        if (key === 'type') {
            setForm((prev) => {
                const next = { ...prev, type: value };
                if (value === 'birthday') {
                    next.birthday_window =
                        rewardsettings?.birthday_window || prev.birthday_window || 'week';
                }
                return next;
            });
            return;
        }
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!form.title.trim()) {newErrors.title = 'Title is required';}
        if (!form.description.trim()) {newErrors.description = 'Description is required';}
        if (form.type === 'birthday' && !BIRTHDAY_REWARDS_UI_ENABLED) {
            newErrors.general = 'Birthday rewards are not available in the dashboard yet.';
        }
        if ((form.type === 'visits' || form.type === 'punch') && !VISIT_AND_PUNCH_CARD_REWARDS_UI_ENABLED) {
            newErrors.general = 'Visits and punch card rewards are not available yet.';
        } else if (form.type === 'punch' && !rewardsettings?.punch_cards_enabled) {
            newErrors.general =
                'Turn on Enable Punch Card Rewards in Rewards Settings to create a punch card reward.';
        }
        if (form.type !== 'birthday' && form.cost <= 0)
            {newErrors.cost = 'Cost must be greater than 0';}
        if (form.type !== 'birthday' && form.hasMaxRedemptions && form.max_redemptions <= 0)
            {newErrors.max_redemptions = 'Max redemption must be greater than 0';}
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {return;}
        try {
            setFormLoading(true);
            const submitForm = { ...form };
            if (!form.hasMaxRedemptions) {submitForm.max_redemptions = null;}
            if (form.type === 'birthday') {
                submitForm.birthdayWindow =
                    form.birthday_window || rewardsettings?.birthday_window || 'week';
                delete submitForm.birthday_window;
                delete submitForm.cost;
                delete submitForm.hasMaxRedemptions;
                delete submitForm.max_redemptions;
            } else {
                delete submitForm.birthday_window;
                delete submitForm.birthdayWindow;
            }
            if (submitForm.type === 'punch') {
                submitForm.type = 'visits';
            }
            await dispatch(rewardsSliceAPI.createReward(submitForm)).unwrap();

            setForm({
                type: 'points',
                title: '',
                description: '',
                cost: 0,
                max_redemptions: 1,
                hasMaxRedemptions: false,
                birthday_window: rewardsettings?.birthday_window || 'week',
            });
            setErrors({});
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            const errorMessage = extractErrorMessage(err);
            logger.error('Failed to save reward', { userId, rewardData: form, error: errorMessage });
            
            // Check if it's a tier limit error
            if (errorMessage.includes('limit exceeded') || errorMessage.includes('Please upgrade')) {
                setWarningMessage(errorMessage);
                setShowWarningModal(true);
                setErrors({});
            } else {
                setErrors({ general: errorMessage });
            }
        } finally {
            setFormLoading(false);
        }
    };

    const handleDeleteClick = (id) => {
        setSelectedRewardId(id);
        setShowModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedRewardId) {
            return;
        }
        try {
            await dispatch(rewardsSliceAPI.deleteReward({ id: selectedRewardId })).unwrap();
            setShowModal(false);
            setSelectedRewardId(null);
        } catch (err) {
            const message = extractErrorMessage(err);
            logger.error('Failed to delete reward', { userId, rewardId: selectedRewardId, error: message });
        }
    };

    const handleModalClose = () => {
        setShowModal(false);
        setSelectedRewardId(null);
    };

    const isEnabled = (type) => {
        if (type === 'visits') {
            return !!rewardsettings?.visits_enabled && VISIT_AND_PUNCH_CARD_REWARDS_UI_ENABLED;
        }
        if (type === 'punch') {
            return (
                !!rewardsettings?.visits_enabled &&
                !!rewardsettings?.punch_cards_enabled &&
                VISIT_AND_PUNCH_CARD_REWARDS_UI_ENABLED
            );
        }
        if (type === 'points') {
            return !!rewardsettings?.points_enabled;
        }
        if (type === 'birthday') {
            return !!rewardsettings?.birthday_enabled && BIRTHDAY_REWARDS_UI_ENABLED;
        }
        return false;
    };

    if (loading && !hasLoaded) {
        return (
            <div className="p-4 sm:p-6 w-full max-w-7xl mx-auto">
                <div className="flex items-center justify-center h-64">
                    <div className="text-lg text-gray-600">Loading rewards...</div>
                </div>
            </div>
        );
    }

    if (error && !hasLoaded) {
        return (
            <div className="p-4 sm:p-6 w-full max-w-7xl mx-auto">
                <div className="flex items-center justify-center h-64">
                    <div className="text-lg text-red-600">
                        Error loading rewards:{' '}
                        {typeof error === 'string' ? error : error.error || JSON.stringify(error)}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-6 w-full max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-primary">Manage Rewards</h2>
                <div className="flex gap-2">
                    <button
                        onClick={handleRefresh}
                        disabled={loading}
                        className="flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 text-sm sm:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                    {hasPermission('create_reward') && (
                    <button
                        onClick={() => setShowForm((prev) => !prev)}
                        className="flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 text-sm sm:text-base bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                        {showForm ? 'Hide Form' : 'Add Reward'}
                    </button>
                    )}
                </div>
            </div>
            <RewardAnalytics analytics={analytics} />
            <div className="flex flex-col md:flex-row md:space-x-6 space-y-6 md:space-y-0">
                <RewardList
                    rewards={paginatedRewards}
                    isEnabled={isEnabled}
                    onDelete={handleDeleteClick}
                    canDelete={hasPermission('delete_reward')}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                />
                {showForm && (
                    <RewardForm
                        form={form}
                        errors={errors}
                        formLoading={formLoading}
                        success={success}
                        rewardsettings={rewardsettings}
                        isEnabled={isEnabled}
                        onFormChange={handleFormChange}
                        onSubmit={handleSubmit}
                    />
                )}
            </div>
            <RewardDeleteModal
                isOpen={showModal}
                onClose={handleModalClose}
                onConfirm={handleDeleteConfirm}
            />
            <TierLimitWarningModal
                isOpen={showWarningModal}
                onClose={() => setShowWarningModal(false)}
                message={warningMessage}
            />
        </div>
    );
}
